// @ts-nocheck
import { format, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

// Default timezone (MST) - used as fallback
export const DEFAULT_TIMEZONE = 'America/Denver';

/**
 * Format a date in a specific timezone
 * @param {Date|string} date - Date object or ISO string
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/Chicago')
 * @param {string} formatString - Format string (default: 'MMM d, yyyy h:mm a')
 * @returns {string} Formatted date string in the specified timezone
 */
export const formatInTimezone = (date, timezone = DEFAULT_TIMEZONE, formatString = 'MMM d, yyyy h:mm a') => {
  if (!date) return '';
  
  try {
    let dateStr = typeof date === 'string' ? date : date.toISOString();
    // Ensure the date string is treated as UTC if it has no timezone indicator
    if (typeof date === 'string' && !date.endsWith('Z') && !date.includes('+') && !/[-+]\d{2}:\d{2}$/.test(date)) {
      dateStr = date + 'Z';
    }
    const dateObj = parseISO(dateStr);
    return formatInTimeZone(dateObj, timezone, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Legacy function - Format a date in MST timezone
 * @deprecated Use formatInTimezone with timezone parameter instead
 */
export const formatMST = (date, formatString = 'MMM d, yyyy h:mm a') => {
  return formatInTimezone(date, DEFAULT_TIMEZONE, formatString);
};

/**
 * Format date as "MMM d, yyyy" in specified timezone
 */
export const formatDate = (date, timezone = DEFAULT_TIMEZONE) => {
  return formatInTimezone(date, timezone, 'MMM d, yyyy');
};

/**
 * Format date with time as "MMM d, yyyy h:mm a" in specified timezone
 */
export const formatDateTime = (date, timezone = DEFAULT_TIMEZONE) => {
  return formatInTimezone(date, timezone, 'MMM d, yyyy h:mm a');
};

/**
 * Format time only as "h:mm a" in specified timezone
 */
export const formatTime = (date, timezone = DEFAULT_TIMEZONE) => {
  return formatInTimezone(date, timezone, 'h:mm a');
};

/**
 * Legacy functions - Format in MST
 * @deprecated Use formatDate, formatDateTime, formatTime with timezone parameter
 */
export const formatDateMST = (date) => formatDate(date, DEFAULT_TIMEZONE);
export const formatDateTimeMST = (date) => formatDateTime(date, DEFAULT_TIMEZONE);
export const formatTimeMST = (date) => formatTime(date, DEFAULT_TIMEZONE);

/**
 * Format as relative time in specified timezone
 */
export const formatRelative = (date, timezone = DEFAULT_TIMEZONE) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const now = new Date();
    const tzDate = toZonedTime(dateObj, timezone);
    const tzNow = toZonedTime(now, timezone);
    
    const diffMs = tzNow - tzDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return formatDate(date, timezone);
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return '';
  }
};

/**
 * Legacy function
 * @deprecated Use formatRelative with timezone parameter
 */
export const formatRelativeMST = (date) => formatRelative(date, DEFAULT_TIMEZONE);

/**
 * Get current date/time in specified timezone
 */
export const nowInTimezone = (timezone = DEFAULT_TIMEZONE) => {
  return toZonedTime(new Date(), timezone);
};

/**
 * Convert a date to specified timezone
 */
export const toTimezone = (date, timezone = DEFAULT_TIMEZONE) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return toZonedTime(dateObj, timezone);
};

/**
 * Legacy functions
 * @deprecated Use nowInTimezone and toTimezone with timezone parameter
 */
export const nowMST = () => nowInTimezone(DEFAULT_TIMEZONE);
export const toMST = (date) => toTimezone(date, DEFAULT_TIMEZONE);

/**
 * Format a date-only string (YYYY-MM-DD) as a readable date without timezone shift.
 * Date-only strings represent calendar dates, not points in time,
 * so "2026-02-15" should always display as "Feb 15, 2026" regardless of timezone.
 */
export const formatCalendarDate = (dateStr, formatString = 'MMM d, yyyy') => {
  if (!dateStr) return '';
  try {
    const str = String(dateStr);
    const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      // Parse at noon UTC to prevent any timezone from shifting the day
      const [, year, month, day] = dateOnly;
      const d = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
      return format(d, formatString);
    }
    // For full datetime strings, format with date-fns (browser TZ)
    const dateObj = typeof str === 'string' ? parseISO(str) : str;
    return format(dateObj, formatString);
  } catch (error) {
    console.error('Error formatting calendar date:', error);
    return String(dateStr) || '';
  }
};

/**
 * Get today's date as YYYY-MM-DD string in the specified timezone.
 * Use instead of new Date().toISOString().split('T')[0] which gives the UTC date
 * and can be off by a day depending on the user's timezone.
 */
export const getTodayInTimezone = (timezone = DEFAULT_TIMEZONE) => {
  try {
    // en-CA locale formats as YYYY-MM-DD
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());
  } catch {
    const now = new Date();
    return now.getFullYear() + '-' +
      String(now.getMonth() + 1).padStart(2, '0') + '-' +
      String(now.getDate()).padStart(2, '0');
  }
};